import { Box, Tabs } from '@chakra-ui/react';
import React from 'react';

/**
 * @useFor 3rd-tier sub-navigation **under** a `PageHeader`.
 *
 * Examples in-tree:
 *   - Account > People > {Users, Roles, SSO}
 *     (`apps/aegix-sms-react/src/components/admin/AuthorizationContent.tsx`)
 *   - Sites > Protocols > {Site protocols, District defaults}
 *     (`apps/aegix-sms-react/src/components/sites/ProtocolsList.tsx`)
 *
 * Use this when you have 2–4 mutually-exclusive content views beneath
 * a primary page tab. Auto-hides when `tabs.length <= 1` so consumers
 * can drive visibility by conditionally including a tab rather than
 * gating the whole component.
 *
 * Pass `hideWhenSingle={false}` when the strip is persistent navigation
 * chrome whose presence must not depend on how many entries a given role
 * or feature flag leaves visible — a lone tab is still the surface the
 * user navigates by, and hiding it strands them on a blank shell.
 *
 * **Do NOT use** for:
 *   - **2nd-tier page tabs** (Floor Plans / Resources / Protocols on the
 *     Sites page) — those live inside `PageHeader.tabs`, not this
 *     component.
 *   - **Toggle-style stateful filter pills** (e.g. urgency: All /
 *     Emergency / Attention / Follow on the Protocols list) — use
 *     `TableOptions` or `ToggleGroup`. TabMenu is for *navigation*
 *     between views, not for *filtering* within a single view.
 *   - **Single binary toggles** (light/dark, on/off) — use `toggle`
 *     (`ToggleButton`) instead.
 *
 * See `libs/themes/aegix/CONVENTIONS.md` for the full pattern map.
 */
export interface TabItem {
  value: string;
  label: string;
  icon?: React.ElementType | React.ReactElement;
  [key: string]: unknown;
  'data-e2e'?: string;
}

interface TabMenuProps {
  tabs: TabItem[];
  activeTab: string;
  onChange: (value: string) => void;
  e2eTitle?: string;
  /** `false` keeps a lone tab rendered. Defaults to the auto-hide behaviour. */
  hideWhenSingle?: boolean;
}

function renderIcon(icon: React.ElementType | React.ReactElement) {
  if (React.isValidElement(icon)) {
    return icon;
  }
  const Icon = icon as React.ElementType;
  return <Icon size={20} />;
}

export function TabMenu({
  tabs,
  activeTab,
  onChange,
  e2eTitle,
  hideWhenSingle = true,
}: TabMenuProps) {
  if (tabs.length === 0 || (hideWhenSingle && tabs.length <= 1)) {
    return null;
  }

  return (
    <Tabs.Root
      value={activeTab}
      onValueChange={(e) => onChange(e.value)}
      variant="plain"
      data-e2e={e2eTitle}
    >
      <Box
        borderBottom="1px solid"
        borderColor="border.secondary"
        flexShrink={0}
      >
        <Tabs.List
          px={4}
          gap={2}
          width="100%"
          justifyContent="flex-start"
          overflowX="auto"
          flexWrap="nowrap"
          css={{
            '&::-webkit-scrollbar': { display: 'none' },
            scrollbarWidth: 'none',
          }}
        >
          {tabs.map((tab) => (
            <Tabs.Trigger
              key={tab.value}
              value={tab.value}
              minWidth="fit-content"
              px={3}
              gap={2}
              borderRadius="md"
              cursor="pointer"
              color="text.secondary"
              fontSize="14px"
              fontWeight={500}
              textTransform="capitalize"
              whiteSpace="nowrap"
              bg="transparent"
              _selected={{
                color: 'text.action',
                fontWeight: 600,
                bg: 'surface.secondary',
              }}
              _hover={{ bg: 'surface.secondary', color: 'text.primary' }}
              data-e2e={tab['data-e2e']}
            >
              {tab.icon && renderIcon(tab.icon)}
              {tab.label}
            </Tabs.Trigger>
          ))}
          <Tabs.Indicator display="none" />
        </Tabs.List>
      </Box>
    </Tabs.Root>
  );
}
